import { createFileRoute, Link } from "@tanstack/react-router";
import equipmentImg from "@/assets/dj-mariage-equipment.jpg";
import sparksImg from "@/assets/dj-mariage-sparks.jpg";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { PageHero } from "@/components/page-hero";
import { SeoAccordion } from "@/components/seo-accordion";
import { MARIAGE_SECTIONS } from "@/data/seo-blocks";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";
const FAQ = [
  {
    q: "Que contient un pack DJ mariage sono + lumière ?",
    a: "Le pack regroupe la prestation DJ (5 h à 10 h selon formule), la sonorisation cérémonie + soirée (RCF, Electro-Voice EKX-15P), l'éclairage LED de la salle et du dancefloor (lyres Chauvet Rogue), les micros HF Shure pour discours et cérémonie laïque, et la régie Pioneer CDJ-3000 · DJM-A9. Le matériel est livré, installé, testé puis démonté par DJ Val.",
  },
  {
    q: "Quel pack DJ mariage choisir pour 100 invités dans le Vaucluse ?",
    a: "Pour un mariage de 80 à 150 invités, la formule Signature (à partir de 1 490 €) est la plus demandée : 8 h d'animation, sono cérémonie + soirée, mise en lumière complète de la salle, machine à fumée légère pour l'ouverture de bal. Au-delà de 200 invités ou pour un lieu avec plusieurs zones, basculez sur Prestige (multi-zones, lyres + lasers).",
  },
  {
    q: "Combien coûte un pack DJ sono lumière pour mariage ?",
    a: "Les formules mariage démarrent à partir de 1 490 € : Signature 1 490 € (8 h, 150 personnes, la plus demandée), Prestige 2 190 € (10 h, 250 personnes, lyres + lasers + étincelles froides). Pas de TVA (art. 293 B CGI), acompte 30 % à la réservation.",
  },
  {
    q: "Le pack mariage inclut-il les étincelles froides ?",
    a: "Option incluse dans le pack Prestige, en supplément (+150 €) pour les formules Essentielle et Signature. Les machines pyro froides sont 100 % sans danger, utilisables en intérieur sans permis ni autorisation préfectorale.",
  },
  {
    q: "Couvrez-vous tout le Vaucluse et les Bouches-du-Rhône ?",
    a: "Oui — Avignon, Cavaillon, Aix-en-Provence, Arles, Salon, Nîmes, Orange, Carpentras, Saint-Rémy, Pertuis, Apt, Uzès, Luberon, Alpilles. Aucun frais de déplacement dans un rayon de 80 km autour de Cavaillon, forfait kilométrique transparent au-delà (0,60 €/km).",
  },
  {
    q: "Peut-on louer le matériel sans la prestation DJ ?",
    a: "Oui — toute la sonorisation, l'éclairage et les effets utilisés sur les prestations DJ Val sont aussi disponibles à la location (avec livraison-installation en option). Voir la page Location matériel.",
  },
];

const PACKS = [
  {
    name: "Pack Signature",
    price: "1 490 €",
    duration: "8 h",
    audience: "150 invités",
    bullets: [
      "Sono cérémonie + vin d'honneur + soirée",
      "Mise en lumière complète de la salle",
      "2 micros HF (cérémonie laïque)",
      "Machine fumée légère ouverture bal",
      "Repérage technique du lieu inclus",
    ],
    featured: true,
  },
  {
    name: "Pack Prestige",
    price: "2 190 €",
    duration: "10 h",
    audience: "250 invités",
    bullets: [
      "Sono multi-zones (cérémonie, cocktail, soirée)",
      "Lyres LED Chauvet + lasers + brouillard lourd",
      "Étincelles froides ouverture de bal",
      "Photobooth ou bar à vinyles",
      "Second technicien sur place",
    ],
  },
];

const INCLUDED = [
  { title: "Sonorisation pro", text: "Enceintes RCF et Electro-Voice EKX-15P, subwoofers, processeurs DSP. Couverture homogène cérémonie, vin d'honneur et soirée." },
  { title: "Éclairage LED scénique", text: "Lyres Chauvet Rogue, PARs LED, mise en lumière indirecte de la salle aux couleurs de votre thème." },
  { title: "Micros HF Shure", text: "ULX-D ou Sennheiser EW — discours, cérémonie laïque en extérieur, animation. Capsules main, serre-tête ou cravate." },
  { title: "Régie Pioneer CDJ-3000 · DJM-A9", text: "Le standard club mondial. Mix fluide, transitions précises, son broadcast 96 kHz/32 bits." },
  { title: "Machines à effets", text: "Fumée légère ouverture de bal, brouillard lourd, bulles, confettis, étincelles froides selon formule." },
  { title: "Matériel doublé", text: "Backup complet apporté sur chaque prestation. Si une enceinte ou un micro flanche, le secours prend le relais en 30 secondes." },
];

export const Route = createFileRoute("/dj-mariage/pack-sono-lumiere")({
  head: () => ({
    meta: [
      { title: "Pack DJ mariage sono + lumière | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Pack DJ mariage clés en main en Provence : sono RCF, éclairage LED Chauvet, micros HF Shure, régie Pioneer. Formules de 1 490 € à 2 190 €. Devis gratuit en 24 h.")
      },
      { property: "og:title", content: "Pack DJ Mariage Sono + Lumière — Provence & Vaucluse" },
      {
        property: "og:description",
        content:
          metaDesc("Sono + éclairage + DJ + effets : formules transparentes pour votre mariage dans le Vaucluse, Bouches-du-Rhône, Gard. Dès 1 490 €.")
      },
      { property: "og:url", content: "https://dj-val.fr/dj-mariage/pack-sono-lumiere" },
      { property: "og:image", content: equipmentImg },
      { property: "og:type", content: "product" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/dj-mariage/pack-sono-lumiere" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Pack DJ Mariage Sono + Lumière — Provence & Vaucluse",
          serviceType: "DJ mariage avec sonorisation et éclairage",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: [
            "Avignon", "Cavaillon", "Aix-en-Provence", "Arles", "Nîmes",
            "Orange", "Carpentras", "Saint-Rémy-de-Provence", "Salon-de-Provence",
            "Pertuis", "Apt", "Uzès", "Luberon", "Alpilles",
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "EUR",
            lowPrice: "1490",
            highPrice: "2190",
            offerCount: 2,
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
            { "@type": "ListItem", position: 2, name: "DJ Mariage", item: "https://dj-val.fr/dj-mariage" },
            { "@type": "ListItem", position: 3, name: "Pack Sono + Lumière", item: "https://dj-val.fr/dj-mariage/pack-sono-lumiere" },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={equipmentImg}
        eyebrow="Pack DJ Mariage · Sono + Lumière"
        title={<>Pack DJ mariage <span className="italic">sono + lumière</span></>}
        subtitle="Une seule réservation pour le DJ, la sonorisation pro, l'éclairage scénique LED et les effets de votre mariage. Livraison, installation et démontage inclus dans tout le Vaucluse, Bouches-du-Rhône et Gard."
        primary={{ label: "Devis pack mariage 24 h", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
      />
      <ReviewsProof />


      {/* TLDR — AIO ready */}
      <div className="px-6 pt-12">
        <TldrBox
          summary="Pack DJ mariage clés en main : sono RCF + Electro-Voice, éclairage LED Chauvet, micros HF Shure, régie Pioneer CDJ-3000 · DJM-A9. Formules de 1 490 € à 2 190 €. Livraison-installation incluses en Provence."
          items={[
            { label: "Formules", value: "Signature 1 490 € · Prestige 2 190 €" },
            { label: "Capacité sono", value: "De 80 à 250 invités" },
            { label: "Zone couverte", value: "Vaucluse, Bouches-du-Rhône, Gard, Drôme sud" },
            { label: "Délai devis", value: "24 h, sans TVA (art. 293 B CGI)" },
          ]}
        />
      </div>

      {/* PACKS */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-14 sr" data-reveal>
          <p className="eyebrow text-foreground">Les 3 formules</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Choisissez votre <span className="italic">pack mariage</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {PACKS.map((p, i) => (
            <div
              key={p.name}
              className={`relative border p-10 sr flex flex-col ${
                p.featured ? "border-[var(--gold)] bg-card" : "border-foreground/10"
              }`}
              data-reveal
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {p.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--gold)] text-[var(--ink)] px-4 py-1 text-[9px] font-semibold uppercase tracking-[0.3em]">
                  Le + demandé
                </span>
              )}
              <h3 className="font-serif-display text-2xl text-foreground">{p.name}</h3>
              <p className="text-2xl text-foreground font-light mt-4 font-serif-display italic">
                à partir de {p.price}
              </p>
              <div className="flex gap-3 mt-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span>{p.duration}</span><span>·</span><span>{p.audience}</span>
              </div>
              <span className="gold-rule !w-8 my-6" />
              <ul className="space-y-3 text-sm flex-1">
                {p.bullets.map((b) => (
                  <li key={b} className="flex gap-3"><span className="text-[var(--gold)] mt-0.5">✦</span><span>{b}</span></li>
                ))}
              </ul>
              <Link to="/contactez-nous" className="mt-8 block text-center bg-[var(--gold)] text-[var(--ink)] py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
                Demander ce pack
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 text-xs text-muted-foreground italic">
          Voir le <Link to="/tarifs" className="underline hover:text-[var(--gold)]">comparatif détaillé et les options à la carte</Link>.
        </p>
      </section>

      {/* INCLUDED */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 sr" data-reveal>
            <p className="eyebrow text-foreground">Ce qui est inclus</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">
              Le matériel <span className="italic">pro</span> que j'apporte
            </h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="grid gap-px bg-foreground/10 md:grid-cols-2 lg:grid-cols-3 border border-foreground/10">
            {INCLUDED.map((g, i) => (
              <div key={g.title} className="bg-background p-8 sr" data-reveal style={{ transitionDelay: `${i * 50}ms` }}>
                <p className="font-serif-display italic text-foreground text-xl">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif-display text-xl text-foreground mt-3">{g.title}</h3>
                <span className="gold-rule !w-8 my-3" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light">{g.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS / CROSS-SELL */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/location-materiel" className="group border border-foreground/10 bg-card p-8 hover:border-[var(--gold)] transition-colors block">
            <p className="eyebrow text-foreground">Location seule</p>
            <h3 className="font-serif-display text-2xl text-foreground mt-3">Louer le matériel sans le DJ</h3>
            <p className="text-sm text-muted-foreground mt-3 font-light">Sono, éclairage, photobooth, vidéoprojecteur : tout est aussi disponible à la location dans le Vaucluse.</p>
            <span className="mt-5 inline-block text-[var(--gold)] text-xs uppercase tracking-[0.25em] group-hover:text-foreground">Voir le catalogue →</span>
          </Link>
          <Link to="/tarifs" className="group border border-foreground/10 bg-card p-8 hover:border-[var(--gold)] transition-colors block">
            <p className="eyebrow text-foreground">Comparatif</p>
            <h3 className="font-serif-display text-2xl text-foreground mt-3">Tarifs détaillés & options</h3>
            <p className="text-sm text-muted-foreground mt-3 font-light">Tableau comparatif des 3 formules, options à la carte (étincelles froides, photobooth, vidéo).</p>
            <span className="mt-5 inline-block text-[var(--gold)] text-xs uppercase tracking-[0.25em] group-hover:text-foreground">Voir les tarifs →</span>
          </Link>
          <Link to="/soirees-evenements" className="group border border-foreground/10 bg-card p-8 hover:border-[var(--gold)] transition-colors block">
            <p className="eyebrow text-foreground">Autres événements</p>
            <h3 className="font-serif-display text-2xl text-foreground mt-3">Soirées privées & entreprise</h3>
            <p className="text-sm text-muted-foreground mt-3 font-light">Anniversaires, soirées d'entreprise, baptêmes, communions, événements thématiques.</p>
            <span className="mt-5 inline-block text-[var(--gold)] text-xs uppercase tracking-[0.25em] group-hover:text-foreground">Voir les prestations →</span>
          </Link>
        </div>
      </section>

      {/* SPARKS BANNER */}
      <section className="on-dark relative py-24 px-6 bg-cover bg-center" style={{ backgroundImage: `url(${sparksImg})` }}>
        <div className="absolute inset-0 bg-[var(--ink)]/85" />
        <div className="relative max-w-3xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-[var(--gold)]">+200 mariages animés en Provence</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4 leading-tight">
            Un pack <span className="italic gold-text-shine">clés en main</span>, zéro logistique pour vous
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-sm text-foreground/90 font-light leading-relaxed">
            Un seul interlocuteur, un seul contrat, un seul devis. J'arrive 4 h avant l'événement pour l'installation, je teste tout, j'anime, je démonte. Vous n'avez à gérer ni transport, ni câbles, ni inquiétude technique.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-card border-y border-foreground/10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 sr" data-reveal>
            <p className="eyebrow text-foreground">Questions fréquentes</p>
            <h2 className="font-serif-display text-4xl text-foreground mt-4">FAQ pack mariage</h2>
            <span className="gold-rule mx-auto !w-12 mt-6" />
          </div>
          <div className="space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group border border-foreground/10 bg-background p-5 sr" data-reveal>
                <summary className="cursor-pointer font-serif-display text-lg text-foreground flex justify-between items-center gap-4">
                  {f.q}
                  <span className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed font-light">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-cover bg-center border-y border-foreground/10" style={{ backgroundImage: `url(${receptionImg})` }}>
        <div className="absolute inset-0" />
        <div className="relative max-w-2xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Réservez votre date</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4">
            On <span className="italic">cale votre pack</span> ?
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Demander un devis sous 24 h
          </Link>
        </div>
      </section>

      <SeoAccordion
        eyebrow="Pack DJ Mariage · Aller plus loin"
        title={<>Tout sur les <span className="italic">packs mariage</span></>}
        intro="Ce qui est inclus, la logistique, le matériel pro et les contraintes spécifiques des lieux du Vaucluse."
        sections={MARIAGE_SECTIONS}
      />
    </div>
  );
}
